import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEtapas, useMembros, useProjetos, useTarefas } from '../hooks/useProjetos';
import { cores } from '../styles/cores';
import { BarraProgresso, Btn, Campo, EstadoVazio, Input, Modal, Select, Spinner } from '../components/UI';

const FILTROS = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'pendentes', rotulo: 'Pendentes' },
  { valor: 'concluidas', rotulo: 'Concluídas' },
];

const CORES_PRIORIDADE = {
  alta: { cor: cores.erro, fundo: '#FBE6E6', rotulo: 'alta' },
  media: { cor: cores.alerta, fundo: '#FCEFDB', rotulo: 'média' },
  baixa: { cor: cores.sucesso, fundo: '#E1F5EE', rotulo: 'baixa' },
};

const paraDataInput = (dataBR) => {
  if (!dataBR) return '';
  const [dia, mes, ano] = dataBR.split('/');
  if (!dia || !mes || !ano) return '';
  return `${ano}-${mes}-${dia}`;
};

const paraDataBR = (dataInput) => {
  if (!dataInput) return '';
  const [ano, mes, dia] = dataInput.split('-');
  return `${dia}/${mes}/${ano}`;
};

function AnelProgresso({ percentual, concluidas, total }) {
  const tamanho = 80;
  const raio = 34;
  const circunferencia = 2 * Math.PI * raio;
  const offset = circunferencia - (percentual / 100) * circunferencia;
  return (
    <div style={{ position: 'relative', width: tamanho, height: tamanho }}>
      <svg width={tamanho} height={tamanho} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={tamanho / 2} cy={tamanho / 2} r={raio} stroke={cores.card} strokeWidth={8} fill="none" />
        <circle
          cx={tamanho / 2} cy={tamanho / 2} r={raio}
          stroke={cores.primario} strokeWidth={8} fill="none"
          strokeDasharray={circunferencia} strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <strong style={{ fontSize: 16, color: cores.primario }}>{percentual}%</strong>
        <span style={{ fontSize: 11, color: cores.textoSecundario }}>{concluidas}/{total}</span>
      </div>
    </div>
  );
}

const FORM_VAZIO = { nome: '', descricao: '', responsavel: '', prioridade: 'media', dataPrevista: '' };

export function Tarefas() {
  const { projetoId, etapaId } = useParams();
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const { projetos, carregando: carregandoProjetos } = useProjetos();
  const { etapas, carregando: carregandoEtapas } = useEtapas(projetoId);
  const { tarefas, carregando: carregandoTarefas, criarTarefa, alternarTarefa, atualizarTarefa, excluirTarefa } =
    useTarefas(projetoId, etapaId);

  const [filtro, setFiltro] = useState('todas');
  const [novaTarefa, setNovaTarefa] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [tarefaEditando, setTarefaEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [expandidas, setExpandidas] = useState(new Set());

  const toggleExpandir = (id) => {
    setExpandidas((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const projeto = projetos.find((p) => p.id === projetoId);
  const etapa = etapas.find((e) => e.id === etapaId);

  // UIDs dos membros do projeto para o dropdown de responsável
  const membrosUids = useMemo(() => {
    if (!projeto) return [];
    return [...new Set([projeto.ownerUid, ...(projeto.membroUids || [])].filter(Boolean))];
  }, [projeto?.ownerUid, projeto?.membroUids?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const membros = useMembros(membrosUids);

  if (carregandoProjetos || carregandoEtapas || carregandoTarefas) return <Spinner />;
  if (!projeto || !etapa) return <EstadoVazio titulo="Etapa não encontrada" />;

  // Permissões
  const ehDono = projeto.ownerUid === usuario?.uid;
  const nomeUsuario = (usuario?.nome || '').toLowerCase().trim();
  const permissao = projeto.membrosPermissoes?.[usuario?.uid];
  const estaBloqueado = !ehDono && permissao === 'somente_leitura';

  const podeAlternarTarefa = (tarefa) => {
    if (ehDono) return true;
    if (estaBloqueado) return false;
    return (tarefa.responsavel || '').toLowerCase().trim() === nomeUsuario && nomeUsuario !== '';
  };

  const total = tarefas.length;
  const concluidas = tarefas.filter((t) => t.concluida).length;
  const percentual = total > 0 ? Math.round((concluidas / total) * 100) : 0;

  const tarefasFiltradas = tarefas.filter((t) => {
    if (filtro === 'pendentes') return !t.concluida;
    if (filtro === 'concluidas') return t.concluida;
    return true;
  });

  const adicionarTarefa = async (e) => {
    e.preventDefault();
    if (!novaTarefa.trim()) return;
    await criarTarefa({ nome: novaTarefa.trim(), descricao: '', responsavel: '', prioridade: 'media', dataPrevista: '' });
    setNovaTarefa('');
  };

  const abrirEdicao = (tarefa) => {
    setTarefaEditando(tarefa);
    setForm({
      nome: tarefa.nome || '',
      descricao: tarefa.descricao || '',
      responsavel: tarefa.responsavel || '',
      prioridade: tarefa.prioridade || 'media',
      dataPrevista: paraDataInput(tarefa.dataPrevista),
    });
    setModalAberto(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    await atualizarTarefa(tarefaEditando.id, {
      nome: form.nome,
      descricao: form.descricao,
      responsavel: form.responsavel,
      prioridade: form.prioridade,
      dataPrevista: paraDataBR(form.dataPrevista),
    });
    setSalvando(false);
    setModalAberto(false);
    setTarefaEditando(null);
  };

  const excluir = async (e, tarefa) => {
    e.stopPropagation();
    if (window.confirm(`Excluir a tarefa "${tarefa.nome}"?`)) {
      await excluirTarefa(tarefa.id);
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: cores.textoSecundario, marginBottom: 16 }}>
        <span style={{ cursor: 'pointer', color: cores.primario }} onClick={() => navegar('/')}>Dashboard</span>
        {' > '}
        <span style={{ cursor: 'pointer', color: cores.primario }} onClick={() => navegar(`/projetos/${projetoId}`)}>
          {projeto.nome}
        </span>
        {' > '}
        {etapa.nome}
      </div>

      {/* Hero */}
      <div
        style={{
          backgroundColor: '#fff',
          border: `1px solid ${cores.card}`,
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 18, color: cores.texto }}>Tarefas — {etapa.nome}</h1>
            {!ehDono && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: estaBloqueado ? cores.erro : cores.textoSecundario,
                  backgroundColor: estaBloqueado ? '#FBE6E6' : cores.card,
                  padding: '3px 10px',
                  borderRadius: 999,
                }}
              >
                {estaBloqueado ? '🔒 Somente leitura' : 'Convidado'}
              </span>
            )}
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: cores.textoSecundario }}>
            {ehDono
              ? 'Marque as tarefas conforme forem concluídas · o progresso atualiza automaticamente'
              : estaBloqueado
              ? 'Seu acesso está em modo somente leitura. Você não pode alterar o status das tarefas.'
              : 'Você pode marcar como concluídas somente as tarefas onde você é o responsável'}
          </p>
          <div style={{ minWidth: 240, marginBottom: 6 }}>
            <BarraProgresso percentual={percentual} />
          </div>
          <span style={{ fontSize: 12, color: cores.textoSecundario }}>
            {concluidas} concluídas de {total}
          </span>
        </div>
        <AnelProgresso percentual={percentual} concluidas={concluidas} total={total} />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {FILTROS.map((item) => (
          <button
            key={item.valor}
            onClick={() => setFiltro(item.valor)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: `1px solid ${filtro === item.valor ? cores.primario : cores.card}`,
              backgroundColor: filtro === item.valor ? cores.primario : '#fff',
              color: filtro === item.valor ? '#fff' : cores.texto,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      {/* Adicionar tarefa — apenas dono */}
      {ehDono && (
        <form onSubmit={adicionarTarefa} style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <Input value={novaTarefa} onChange={(e) => setNovaTarefa(e.target.value)} placeholder="Adicionar nova tarefa..." />
          <Btn type="submit" style={{ flexShrink: 0 }}>+ Adicionar</Btn>
        </form>
      )}

      {/* Lista */}
      {tarefasFiltradas.length === 0 ? (
        <EstadoVazio titulo="Nenhuma tarefa encontrada" descricao="Adicione uma nova tarefa ou ajuste o filtro." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tarefasFiltradas.map((tarefa) => {
            const prioridade = CORES_PRIORIDADE[tarefa.prioridade] || CORES_PRIORIDADE.media;
            const podeAlternar = podeAlternarTarefa(tarefa);
            const expandida = expandidas.has(tarefa.id);
            const temDescricao = !!(tarefa.descricao || '').trim();

            return (
              <div
                key={tarefa.id}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${expandida ? cores.primario : cores.card}`,
                  borderRadius: 10,
                  padding: '12px 16px',
                  opacity: tarefa.concluida ? 0.55 : 1,
                  transition: 'border-color 0.15s',
                }}
              >
                {/* Linha principal */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Checkbox */}
                  <button
                    onClick={() => podeAlternar && alternarTarefa(tarefa.id, tarefa.concluida, { nome: tarefa.nome, criadoEm: tarefa.criadoEm })}
                    title={!podeAlternar ? (estaBloqueado ? 'Acesso bloqueado — somente leitura' : 'Você não é o responsável por esta tarefa') : undefined}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: `2px solid ${tarefa.concluida ? cores.sucesso : podeAlternar ? cores.card : cores.textoTerciario}`,
                      backgroundColor: tarefa.concluida ? cores.sucesso : 'transparent',
                      color: '#fff',
                      cursor: podeAlternar ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      opacity: podeAlternar ? 1 : 0.4,
                    }}
                  >
                    {tarefa.concluida ? '✓' : ''}
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: cores.texto,
                        textDecoration: tarefa.concluida ? 'line-through' : 'none',
                        marginBottom: 2,
                      }}
                    >
                      {tarefa.nome}
                    </div>
                    {tarefa.dataPrevista && (
                      <div style={{ fontSize: 12, color: cores.textoSecundario }}>
                        📅 Prev: {tarefa.dataPrevista}
                      </div>
                    )}
                  </div>

                  {tarefa.responsavel && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: cores.textoSecundario, backgroundColor: cores.card, padding: '4px 10px', borderRadius: 999, flexShrink: 0 }}>
                      {tarefa.responsavel}
                    </span>
                  )}

                  <span style={{ fontSize: 12, fontWeight: 600, color: prioridade.cor, backgroundColor: prioridade.fundo, padding: '4px 10px', borderRadius: 999, flexShrink: 0 }}>
                    {prioridade.rotulo}
                  </span>

                  {/* Botão VER */}
                  <Btn
                    variante="secundario"
                    onClick={() => toggleExpandir(tarefa.id)}
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      padding: '5px 12px',
                      color: expandida ? cores.primario : cores.textoSecundario,
                      borderColor: expandida ? cores.primario : undefined,
                    }}
                  >
                    {expandida ? '▲ Ocultar' : '▼ Ver'}
                  </Btn>

                  {/* Editar/Excluir apenas para dono */}
                  {ehDono && (
                    <>
                      <Btn variante="secundario" onClick={() => abrirEdicao(tarefa)} style={{ flexShrink: 0 }}>Editar</Btn>
                      <Btn variante="perigo" onClick={(e) => excluir(e, tarefa)} style={{ flexShrink: 0 }}>Excluir</Btn>
                    </>
                  )}
                </div>

                {/* Descrição expandida */}
                {expandida && (
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: `1px solid ${cores.card}`,
                    }}
                  >
                    <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: cores.textoSecundario, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Descrição
                    </p>
                    {temDescricao
                      ? <p style={{ margin: 0, fontSize: 14, color: cores.texto, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{tarefa.descricao}</p>
                      : <p style={{ margin: 0, fontSize: 13, color: cores.textoTerciario, fontStyle: 'italic' }}>Nenhuma descrição adicionada.</p>
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal editar tarefa */}
      <Modal titulo="Editar tarefa" aberto={modalAberto} aoFechar={() => setModalAberto(false)}>
        <form onSubmit={salvar}>
          <Campo label="Nome*">
            <Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </Campo>
          <Campo label="Descrição">
            <Input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          </Campo>
          <Campo label="Responsável">
            <Select
              value={form.responsavel}
              onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
            >
              <option value="">Nenhum</option>
              {membros.map((m) => (
                <option key={m.uid} value={m.nome || m.email}>
                  {m.nome || m.email}
                </option>
              ))}
            </Select>
          </Campo>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Campo label="Prioridade">
              <Select value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value })}>
                <option value="alta">Alta</option>
                <option value="media">Média</option>
                <option value="baixa">Baixa</option>
              </Select>
            </Campo>
            <Campo label="Data prevista">
              <Input type="date" value={form.dataPrevista} onChange={(e) => setForm({ ...form, dataPrevista: e.target.value })} />
            </Campo>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <Btn type="button" variante="secundario" onClick={() => setModalAberto(false)}>Cancelar</Btn>
            <Btn type="submit" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
